import React, { useEffect, useMemo, useState } from "react";
import {
    TableBody,
    TableCell,
    TableRow,
    Table,
    TableHeader,
    TableHeaderCell,
    TableCellLayout,
    makeStyles,
    tokens,
    CardHeader,
    Button,
    Card,
    Body1,
    Field,
    List,
    useTableFeatures,
    ListItem,
    TableRowId,
    createTableColumn,
    TableColumnDefinition,
    useTableSelection,
    Dialog,
    DialogSurface,
    DialogBody,
    DialogContent,
    DialogActions,
    Text,
    DialogTrigger,
    useTableSort,
    TableColumnId,
} from "@fluentui/react-components";
import { useParams } from "react-router";
import { Group, Panel, Separator } from "react-resizable-panels";
import { IAddon, type ICustomer, type ILicense } from "../IDB";
import { Code } from "../components/Code";
import { CustomerCardFooder } from "../components/CustomerCardFooter";
import { ContactCard20Regular } from "@fluentui/react-icons";
import { ViewLicenseDialog } from "../components/ViewLicenseDialog";
import { licenseMap } from "./LicenseBuilder";
import { Server } from "../apis";

const useStyles = makeStyles({
    page: {
        paddingTop: tokens.spacingVerticalXL,
        height: '100vh',
        width: 'calc(100% - 260px)'
    },
    separator: {
        background: tokens.colorNeutralBackground4,
        height: '4px'
    },
    table: {
        marginTop: tokens.spacingHorizontalS
    },
    card: {
        margin: "auto",
        width: "720px",
        maxWidth: "100%",
    },
    cardBody: {
        width: 'calc(100% + 32px)',
        borderTop: `2px solid #169691`,
        marginLeft: '-16px',
        marginRight: '-16px',
    },
    cardBodyField: {
        paddingLeft: tokens.spacingHorizontalMNudge,
        paddingRight: tokens.spacingHorizontalMNudge,
    },
    alignBase: {
        alignItems: 'baseline',
    },
    textColor: {
        color: 'black'
    }
});

export const CustomerInfo = () => {
    const styles = useStyles();
    const params = useParams();
    const server = useMemo(() => Server.getInstance(), []);

    const [activeLicense, setActiveLicense] = useState<ILicense>();
    const [displayedCustomer, setDisplayedCustomer] = useState<ICustomer>();
    const [licenseFileElement, setLicenseFileElement] = useState<React.JSX.Element>();

    const setLicense = (lic: ILicense) => {
        window.location.hash = lic.code;
        setActiveLicense(lic);
    };

    useEffect(() => {
        const cb = async () => {
            if (params.customer_id) {
                const customer = await server.getCustomer(params.customer_id)
                setDisplayedCustomer(customer);
                const hash = window.location.hash;
                if (hash && hash.length > 1) {
                    const code = hash.substring(1);
                    const foundLic = customer.licenses.find((lic) => lic.code === code);
                    if (foundLic) {
                        setActiveLicense(foundLic);
                    }
                }
            }
        };
        cb();
    }, [params.customer_id]);

    const columns: TableColumnDefinition<ILicense>[] = [
        createTableColumn<ILicense>({
            columnId: 'code',
            compare: (a, b) => {
                return a.code.localeCompare(b.code)
            },
        }),
        createTableColumn<ILicense>({
            columnId: 'created',
            compare: (a, b) => {
                if (new Date(a.created) > new Date(b.created)) {
                    return 1;
                }
                if (new Date(a.created) === new Date(b.created)) {
                    return 0;
                }
                return -1;
            }
        }),
        createTableColumn<ILicense>({
            columnId: 'expiry',
            compare: (a, b) => {
                if (new Date(a.expiry) > new Date(b.expiry)) {
                    return 1;
                }
                if (new Date(a.expiry) === new Date(b.expiry)) {
                    return 0;
                }
                return -1;
            }
        }),
        createTableColumn<ILicense>({
            columnId: 'type',
            compare: (a, b) => {
                return a.code.localeCompare(b.code)
            },
        }),
        createTableColumn<ILicense>({
            columnId: 'invoice',
            compare: (a, b) => {
                return a.code.localeCompare(b.code)
            },
        }),
    ];

    const [selectedRows, setSelectedRows] = React.useState(
        () => new Set<TableRowId>([1])
    );

    const { getRows, sort: { getSortDirection, toggleColumnSort, sort }, } = useTableFeatures(
        {
            columns,
            items: displayedCustomer?.licenses || []
        },
        [
            useTableSelection({
                selectionMode: "single",
                selectedItems: selectedRows,
                onSelectionChange: (e, data) => {
                    setSelectedRows(data.selectedItems);
                }
            }),
            useTableSort({
                defaultSortState: { sortColumn: 'code', sortDirection: "ascending" },
            }),
        ]
    );

    const headerSortProps = (columnId: TableColumnId) => ({
        onClick: (e: React.MouseEvent) => {
            toggleColumnSort(e, columnId);
        },
        sortDirection: getSortDirection(columnId),
    });

    const rows = sort(getRows((row) => {
        return {
            ...row,
            onClick: (e: React.MouseEvent) => setLicense(row.item),
            onKeyDown: (e: React.KeyboardEvent) => {
                if (e.key === " ") {
                    e.preventDefault();
                    setLicense(row.item);
                }
            },
            appearance: row.item.code === activeLicense?.code ? ("brand" as const) : ("none" as const),
        };
    }));

    const closeToExpiration = (): boolean => {
        if (activeLicense) {
            const ex = new Date(activeLicense.expiry)
            ex.setDate(ex.getDate() - 120);
            const today = new Date();
            if (today > ex) {
                return true;
            }
        }

        return false;
    };

    if (!displayedCustomer) return null;

    let visibleLicenseType = null;
    if (activeLicense?.licenseType === "NEW" && activeLicense.history.some(item => item.indexOf("RENEWED") > -1)) {
        visibleLicenseType = "Renewed Commercial License";
    } else {
        if (activeLicense) {
            visibleLicenseType = licenseMap[activeLicense.licenseType];
        }
    }

    return (
        <>
            <Group className={styles.page} orientation="vertical">
                <Panel minSize={200}>
                    <Card className={styles.card} size="large">
                        <CardHeader
                            header={
                                <Body1 style={{ fontSize: tokens.fontSizeBase500 }}>
                                    <b>{displayedCustomer.company.name.toUpperCase()}</b>
                                </Body1>
                            }
                            action={
                                <Dialog>
                                    <DialogTrigger disableButtonEnhancement>
                                        <Button icon={<ContactCard20Regular />}>Contact Info</Button>
                                    </DialogTrigger>
                                    <DialogSurface>
                                        <DialogBody>
                                            <DialogContent>
                                                <Field label="Recipient" orientation="horizontal">
                                                    <Text>{displayedCustomer.name}</Text>
                                                </Field>
                                                <Field label="Email" orientation="horizontal">
                                                    <Text>{displayedCustomer.email}</Text>
                                                </Field>
                                                <Field label="Phone number" orientation="horizontal">
                                                    <Text>{displayedCustomer.phone}</Text>
                                                </Field>
                                                <Field label="Address" orientation="horizontal">
                                                    <Text>{displayedCustomer.address}</Text>
                                                </Field>
                                            </DialogContent>
                                            <DialogActions>
                                                <DialogTrigger disableButtonEnhancement>
                                                    <Button appearance="secondary">Close</Button>
                                                </DialogTrigger>
                                            </DialogActions>
                                        </DialogBody>
                                    </DialogSurface>
                                </Dialog>
                            }
                        />
                        <div className={activeLicense ? styles.cardBody : ""}>
                            {activeLicense ? (
                                <div className={styles.cardBodyField}>
                                    <Code text={activeLicense.code} preserveLineBreaks={false} />
                                    {activeLicense.revoked ? <Field className={styles.alignBase} label="Revoked" orientation="horizontal"><span style={{ color: '#FF7512', fontWeight: 'bold' }}>True</span></Field> : null}
                                    <Field className={styles.alignBase} label="Application type" orientation="horizontal">{`${activeLicense.floating ? "Floating" : "Node-locked"} with ${activeLicense.used}/${activeLicense.allowed} seats used.`}</Field>
                                    <Field className={styles.alignBase} label="License type" orientation="horizontal">{visibleLicenseType}</Field>
                                    <Field className={styles.alignBase} label="Expiration date" orientation="horizontal"><span style={closeToExpiration() ? { color: '#E9B90F', fontWeight: 'bold' } : {}}>{new Date(activeLicense.expiry).toLocaleDateString()}</span></Field>
                                    <Field className={styles.alignBase} label="Created by" orientation="horizontal">{activeLicense.author}</Field>
                                    <Field className={styles.alignBase} label="Invoice" orientation="horizontal">{activeLicense.invoice}</Field>
                                    <Field className={styles.alignBase} label="Addons" orientation="horizontal">
                                        <List>
                                            {(activeLicense?.addons as IAddon[]).map(a => <ListItem key={a.id}>{a.addon}</ListItem>)}
                                        </List>
                                    </Field>
                                </div>
                            ) : null}
                        </div>
                        <CustomerCardFooder
                            activeLicense={activeLicense}
                            closeToExpiration={closeToExpiration()}
                            customer={displayedCustomer}
                            setLicenseFileElement={(ele) => setLicenseFileElement(ele)}
                        />
                    </Card>
                </Panel>
                <Separator className={styles.separator} />
                <Panel>
                    <div style={{ overflow: 'auto', width: '100%', height: '90%' }}>
                        <Table className={styles.table} arial-label="Issued licenese" sortable>
                            <TableHeader>
                                <TableRow>
                                    <TableHeaderCell {...headerSortProps('code')}>Code</TableHeaderCell>
                                    <TableHeaderCell {...headerSortProps('created')}>Created</TableHeaderCell>
                                    <TableHeaderCell {...headerSortProps('expiry')}>Expiration</TableHeaderCell>
                                    <TableHeaderCell {...headerSortProps('type')}>License Type</TableHeaderCell>
                                    <TableHeaderCell {...headerSortProps('invoice')}>Invoice</TableHeaderCell>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map(({ item, onClick, onKeyDown, appearance }) => (
                                    <TableRow
                                        key={item.code}
                                        onClick={onClick}
                                        onKeyDown={onKeyDown}
                                        aria-selected={item.code === activeLicense?.code}
                                        appearance={appearance}
                                    >
                                        <TableCell>
                                            <TableCellLayout>
                                                {item.code}
                                            </TableCellLayout>
                                        </TableCell>
                                        <TableCell>{new Date(item.created).toLocaleDateString()}</TableCell>
                                        <TableCell>{new Date(item.expiry).toLocaleDateString()}</TableCell>
                                        <TableCell>
                                            {item.revoked
                                                ? "REVOKED"
                                                : (item.licenseType === 'NEW' && item.history.some((h) => h.indexOf('RENEWED') > -1) ? 'RENEWED' : item.licenseType)
                                            }
                                        </TableCell>
                                        <TableCell>{item.invoice}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Panel>
            </Group>
            <ViewLicenseDialog licenseFileElement={licenseFileElement} close={() => { setLicenseFileElement(undefined); }} />
        </>
    );
};